import * as THREE from "three";
import type { RailingStyle } from "../types";

/** Un point de la scène du décor, en mètres : x vers l'est, z vers le sud. */
type GroundPoint = [number, number];

/** Un tronçon de barrière, de `a` à `b`, posé à `base` mètres au-dessus du sol. */
export interface RailingEdge {
  a: GroundPoint;
  b: GroundPoint;
  base: number;
}

interface Paint {
  /** De `x[0]` à `x[1]` mètres autour d'un poteau, qui se répète d'une période à l'autre. */
  x: [number, number];
  y: [number, number];
  color: string;
}

interface Infill {
  color: string;
  opacity: number;
  /** Motif d'une maille de `tile` mètres (largeur, hauteur), opaque là où il rend vrai. */
  pattern?: { tile: [number, number]; opaque: (x: number, y: number) => boolean };
}

interface TexturedLook {
  kind: "textured";
  height: number;
  /** Distance entre deux poteaux, en mètres. */
  period: number;
  frame: Paint[];
  capWidth: number;
  /** Un point plein de la texture du cadre, d'où la lisse vue du dessus tire sa couleur. */
  capUv: [number, number];
  infill?: Infill;
  roughness: number;
  metalness: number;
}

/** Poteaux et lisses en volume, comme la clôture du décor : ce que le plat rend mal, le bois surtout. */
interface SolidLook {
  kind: "solid";
  height: number;
  period: number;
  post: { size: number; color: string };
  rails: { y: [number, number]; thickness: number; color: string }[];
  roughness: number;
  metalness: number;
}

type RailingLook = TexturedLook | SolidLook;

const near = (value: number, width: number) => Math.min(value, 1 - value) < width;

const LOOKS: Record<RailingStyle, RailingLook> = {
  glass: {
    kind: "textured",
    height: 1.05,
    period: 1.5,
    frame: [
      { x: [-0.025, 0.025], y: [0, 1.05], color: "#202326" },
      { x: [-0.75, 0.75], y: [1, 1.05], color: "#202326" },
    ],
    capWidth: 0.06,
    capUv: [0.5, 1.025 / 1.05],
    infill: { color: "#cfe6f0", opacity: 0.35 },
    roughness: 0.5,
    metalness: 0.2,
  },
  "glass-high": {
    kind: "textured",
    height: 1.6,
    period: 1.5,
    frame: [
      { x: [-0.025, 0.025], y: [0, 1.6], color: "#202326" },
      { x: [-0.75, 0.75], y: [1.55, 1.6], color: "#202326" },
    ],
    capWidth: 0.06,
    capUv: [0.5, 1.575 / 1.6],
    infill: { color: "#cfe6f0", opacity: 0.35 },
    roughness: 0.5,
    metalness: 0.2,
  },
  mesh: {
    kind: "textured",
    height: 2.2,
    period: 2.5,
    frame: [
      { x: [-0.035, 0.035], y: [0, 2.2], color: "#2f3a33" },
      { x: [-1.25, 1.25], y: [2.15, 2.2], color: "#2f3a33" },
      { x: [-1.25, 1.25], y: [0.03, 0.08], color: "#2f3a33" },
    ],
    capWidth: 0.05,
    capUv: [0.5, 2.175 / 2.2],
    infill: {
      color: "#4a5650",
      opacity: 0.9,
      pattern: { tile: [0.1, 0.1], opaque: (x, y) => near((x + y) % 1, 0.07) || near((x - y + 1) % 1, 0.07) },
    },
    roughness: 0.6,
    metalness: 0.3,
  },
  bars: {
    kind: "textured",
    height: 1.2,
    period: 2,
    frame: [
      { x: [-0.04, 0.04], y: [0, 1.2], color: "#2a2d30" },
      { x: [-1, 1], y: [1.14, 1.2], color: "#2a2d30" },
      { x: [-1, 1], y: [0.08, 0.14], color: "#2a2d30" },
    ],
    capWidth: 0.06,
    capUv: [0.5, 1.17 / 1.2],
    infill: { color: "#2a2d30", opacity: 1, pattern: { tile: [0.12, 1.2], opaque: (x) => x < 0.18 } },
    roughness: 0.55,
    metalness: 0.35,
  },
  wood: {
    kind: "solid",
    height: 1.3,
    period: 2,
    post: { size: 0.12, color: "#7a5636" },
    rails: [
      { y: [0.44, 0.56], thickness: 0.06, color: "#9a7149" },
      { y: [0.99, 1.11], thickness: 0.06, color: "#9a7149" },
    ],
    roughness: 0.9,
    metalness: 0,
  },
  "wood-white": {
    kind: "solid",
    height: 1.3,
    period: 2,
    post: { size: 0.12, color: "#e6e2d9" },
    rails: [
      { y: [0.44, 0.56], thickness: 0.06, color: "#f5f3ee" },
      { y: [0.99, 1.11], thickness: 0.06, color: "#f5f3ee" },
    ],
    roughness: 0.8,
    metalness: 0,
  },
};

export function railingHeight(style: RailingStyle): number {
  return LOOKS[style].height;
}

const TEXTURE_SIZE = 256;
const PATTERN_SIZE = 64;
/** Au-delà de cet angle, un coin porte son propre poteau. */
const CORNER_DEG = 25;
const POINT_EPSILON_M = 0.01;

function rgba(color: string, alpha = 1): [number, number, number, number] {
  const hex = new THREE.Color(color).getHex();
  return [(hex >> 16) & 255, (hex >> 8) & 255, hex & 255, Math.round(alpha * 255)];
}

function texture(data: ConstructorParameters<typeof THREE.DataTexture>[0], width: number, height: number, wrapT: THREE.Wrapping): THREE.DataTexture {
  const result = new THREE.DataTexture(data, width, height, THREE.RGBAFormat);
  result.colorSpace = THREE.SRGBColorSpace;
  result.wrapS = THREE.RepeatWrapping;
  result.wrapT = wrapT;
  result.magFilter = THREE.LinearFilter;
  result.minFilter = THREE.LinearMipmapLinearFilter;
  result.generateMipmaps = true;
  result.anisotropy = 8;
  result.needsUpdate = true;
  return result;
}

/** Une période de la barrière, poteau centré sur le bord : il se recolle d'une période à la suivante. */
function frameTexture({ period, height, frame }: TexturedLook): THREE.DataTexture {
  const data = new Uint8Array(TEXTURE_SIZE * TEXTURE_SIZE * 4);
  for (const { x: [x0, x1], y: [y0, y1], color } of frame) {
    const pixel = rgba(color);
    const rowFrom = Math.max(0, Math.round((y0 / height) * TEXTURE_SIZE));
    const rowTo = Math.min(TEXTURE_SIZE, Math.round((y1 / height) * TEXTURE_SIZE));
    const columnFrom = Math.round((x0 / period) * TEXTURE_SIZE);
    const columnTo = Math.max(columnFrom + 1, Math.round((x1 / period) * TEXTURE_SIZE));
    for (let row = rowFrom; row < rowTo; row++) {
      for (let column = columnFrom; column < Math.min(columnTo, columnFrom + TEXTURE_SIZE); column++) {
        data.set(pixel, (row * TEXTURE_SIZE + (((column % TEXTURE_SIZE) + TEXTURE_SIZE) % TEXTURE_SIZE)) * 4);
      }
    }
  }
  return texture(data, TEXTURE_SIZE, TEXTURE_SIZE, THREE.ClampToEdgeWrapping);
}

function patternTexture(color: string, opaque: (x: number, y: number) => boolean): THREE.DataTexture {
  const data = new Uint8Array(PATTERN_SIZE * PATTERN_SIZE * 4);
  const pixel = rgba(color);
  const clear = rgba(color, 0);
  for (let row = 0; row < PATTERN_SIZE; row++) {
    for (let column = 0; column < PATTERN_SIZE; column++) {
      data.set(opaque((column + 0.5) / PATTERN_SIZE, (row + 0.5) / PATTERN_SIZE) ? pixel : clear, (row * PATTERN_SIZE + column) * 4);
    }
  }
  return texture(data, PATTERN_SIZE, PATTERN_SIZE, THREE.RepeatWrapping);
}

interface RailingMaterials {
  frame: THREE.MeshStandardMaterial;
  infill: THREE.MeshStandardMaterial | null;
}

const materials = new Map<RailingStyle, RailingMaterials>();

/** Partagés par toutes les barrières d'une même finition : un seul appel de dessin chacun pour toute la carte. */
export function railingMaterials(style: RailingStyle): RailingMaterials {
  const existing = materials.get(style);
  if (existing) return existing;
  const look = LOOKS[style];
  if (look.kind === "solid") {
    const solid = {
      frame: new THREE.MeshStandardMaterial({
        name: `railing-${style}-frame`,
        vertexColors: true,
        roughness: look.roughness,
        metalness: look.metalness,
        side: THREE.DoubleSide,
      }),
      infill: null,
    };
    materials.set(style, solid);
    return solid;
  }
  const frame = new THREE.MeshStandardMaterial({
    name: `railing-${style}-frame`,
    map: frameTexture(look),
    alphaTest: 0.5,
    roughness: look.roughness,
    metalness: look.metalness,
    side: THREE.DoubleSide,
  });
  let infill: THREE.MeshStandardMaterial | null = null;
  if (look.infill) {
    const { color, opacity, pattern } = look.infill;
    const map = pattern ? patternTexture(color, pattern.opaque) : null;
    map?.repeat.set(look.period / pattern!.tile[0], look.height / pattern!.tile[1]);
    infill = new THREE.MeshStandardMaterial({
      name: `railing-${style}-infill`,
      color: map ? "#ffffff" : color,
      map,
      transparent: true,
      opacity,
      depthWrite: false,
      roughness: look.roughness,
      metalness: look.metalness,
      side: THREE.DoubleSide,
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 1,
    });
  }
  const result = { frame, infill };
  materials.set(style, result);
  return result;
}

function samePoint(a: GroundPoint, b: GroundPoint): boolean {
  return Math.abs(a[0] - b[0]) < POINT_EPSILON_M && Math.abs(a[1] - b[1]) < POINT_EPSILON_M;
}

function turnDeg(previous: RailingEdge, next: RailingEdge): number {
  const first = Math.atan2(previous.b[1] - previous.a[1], previous.b[0] - previous.a[0]);
  const second = Math.atan2(next.b[1] - next.a[1], next.b[0] - next.a[0]);
  const turn = Math.abs(((second - first) * 180) / Math.PI) % 360;
  return Math.min(turn, 360 - turn);
}

/**
 * Les tronçons qui se suivent sans angle marqué ni changement de niveau, d'un seul tenant : les
 * poteaux s'y espacent régulièrement, quel que soit le nombre de sommets du contour.
 */
export function railingRuns(edges: RailingEdge[]): RailingEdge[][] {
  const runs: RailingEdge[][] = [];
  for (const edge of edges) {
    if (samePoint(edge.a, edge.b)) continue;
    const run = runs[runs.length - 1];
    const last = run?.[run.length - 1];
    if (run && last && samePoint(last.b, edge.a) && last.base === edge.base && turnDeg(last, edge) <= CORNER_DEG) run.push(edge);
    else runs.push([edge]);
  }
  return runs;
}

interface Buffers {
  positions: number[];
  normals: number[];
  uvs: number[];
}

function quad(buffers: Buffers, corners: [number, number, number][], normal: [number, number, number], uv: [number, number][]): void {
  for (const index of [0, 1, 2, 0, 2, 3]) {
    buffers.positions.push(...corners[index]!);
    buffers.normals.push(...normal);
    buffers.uvs.push(...uv[index]!);
  }
}

function geometry({ positions, normals, uvs }: Buffers): THREE.BufferGeometry {
  const result = new THREE.BufferGeometry();
  result.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  result.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
  result.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  return result;
}

interface SolidBuffers {
  positions: number[];
  normals: number[];
  colors: number[];
}

/** Un pavé couché le long de `direction` (unitaire), sans fond : on ne le voit jamais par en dessous. */
function box(
  buffers: SolidBuffers,
  [cx, cz]: GroundPoint,
  [dx, dz]: GroundPoint,
  [halfLength, halfWidth]: [number, number],
  [y0, y1]: [number, number],
  color: THREE.Color,
): void {
  const at = (l: number, w: number, y: number): [number, number, number] => [cx + dx * l - dz * w, y, cz + dz * l + dx * w];
  const faces: [[number, number, number][], [number, number, number]][] = [
    [[at(-halfLength, halfWidth, y0), at(halfLength, halfWidth, y0), at(halfLength, halfWidth, y1), at(-halfLength, halfWidth, y1)], [-dz, 0, dx]],
    [[at(halfLength, -halfWidth, y0), at(-halfLength, -halfWidth, y0), at(-halfLength, -halfWidth, y1), at(halfLength, -halfWidth, y1)], [dz, 0, -dx]],
    [[at(halfLength, halfWidth, y0), at(halfLength, -halfWidth, y0), at(halfLength, -halfWidth, y1), at(halfLength, halfWidth, y1)], [dx, 0, dz]],
    [[at(-halfLength, -halfWidth, y0), at(-halfLength, halfWidth, y0), at(-halfLength, halfWidth, y1), at(-halfLength, -halfWidth, y1)], [-dx, 0, -dz]],
    [[at(-halfLength, -halfWidth, y1), at(-halfLength, halfWidth, y1), at(halfLength, halfWidth, y1), at(halfLength, -halfWidth, y1)], [0, 1, 0]],
  ];
  for (const [corners, normal] of faces) {
    for (const index of [0, 1, 2, 0, 2, 3]) {
      buffers.positions.push(...corners[index]!);
      buffers.normals.push(...normal);
      buffers.colors.push(color.r, color.g, color.b);
    }
  }
}

/**
 * Les poteaux, régulièrement espacés sur chaque tronçon d'un seul tenant, et une lisse par côté :
 * une dizaine de triangles par poteau, le tout en un seul maillage pour toute la carte.
 */
function solidMesh(look: SolidLook, edges: RailingEdge[], material: THREE.Material): THREE.Mesh {
  const buffers: SolidBuffers = { positions: [], normals: [], colors: [] };
  const postColor = new THREE.Color(look.post.color);
  const halfPost = look.post.size / 2;
  for (const run of railingRuns(edges)) {
    const lengths = run.map(({ a, b }) => Math.hypot(b[0] - a[0], b[1] - a[1]));
    const length = lengths.reduce((sum, value) => sum + value, 0);
    const count = Math.max(1, Math.round(length / look.period));
    let edgeIndex = 0;
    let edgeStart = 0;
    for (let post = 0; post <= count; post++) {
      const distance = (length * post) / count;
      while (edgeIndex < run.length - 1 && edgeStart + lengths[edgeIndex]! < distance) edgeStart += lengths[edgeIndex++]!;
      const { a, b, base } = run[edgeIndex]!;
      const t = Math.min(1, (distance - edgeStart) / lengths[edgeIndex]!);
      const direction: GroundPoint = [(b[0] - a[0]) / lengths[edgeIndex]!, (b[1] - a[1]) / lengths[edgeIndex]!];
      box(buffers, [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t], direction, [halfPost, halfPost], [base, base + look.height], postColor);
    }
    run.forEach(({ a, b, base }, index) => {
      const direction: GroundPoint = [(b[0] - a[0]) / lengths[index]!, (b[1] - a[1]) / lengths[index]!];
      const middle: GroundPoint = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      for (const rail of look.rails) {
        box(buffers, middle, direction, [lengths[index]! / 2 + rail.thickness / 2, rail.thickness / 2], [base + rail.y[0], base + rail.y[1]], new THREE.Color(rail.color));
      }
    });
  }
  const result = new THREE.BufferGeometry();
  result.setAttribute("position", new THREE.Float32BufferAttribute(buffers.positions, 3));
  result.setAttribute("normal", new THREE.Float32BufferAttribute(buffers.normals, 3));
  result.setAttribute("color", new THREE.Float32BufferAttribute(buffers.colors, 3));
  const mesh = new THREE.Mesh(result, material);
  mesh.name = "RailingFrame";
  return mesh;
}

/**
 * Les barrières d'une finition, en deux maillages au plus : un panneau vertical et une lisse vue du
 * dessus par tronçon, les poteaux et le remplissage venant des textures. Le coût ne dépend que du
 * nombre de sommets, jamais de la longueur.
 */
function styleMeshes(style: RailingStyle, edges: RailingEdge[]): THREE.Mesh[] {
  const look = LOOKS[style];
  if (look.kind === "solid") return [solidMesh(look, edges, railingMaterials(style).frame)];
  const panels: Buffers = { positions: [], normals: [], uvs: [] };
  const caps: Buffers = { positions: [], normals: [], uvs: [] };
  const capUv = Array.from({ length: 4 }, () => look.capUv);
  for (const run of railingRuns(edges)) {
    const length = run.reduce((sum, { a, b }) => sum + Math.hypot(b[0] - a[0], b[1] - a[1]), 0);
    const periodsPerMeter = Math.max(1, Math.round(length / look.period)) / length;
    let u = 0;
    for (const { a: [ax, az], b: [bx, bz], base } of run) {
      const edgeLength = Math.hypot(bx - ax, bz - az);
      const next = u + edgeLength * periodsPerMeter;
      const top = base + look.height;
      const [nx, nz] = [(bz - az) / edgeLength, -(bx - ax) / edgeLength];
      quad(panels, [[ax, base, az], [bx, base, bz], [bx, top, bz], [ax, top, az]], [nx, 0, nz], [[u, 0], [next, 0], [next, 1], [u, 1]]);
      const [ox, oz] = [(nx * look.capWidth) / 2, (nz * look.capWidth) / 2];
      quad(caps, [[ax - ox, top, az - oz], [bx - ox, top, bz - oz], [bx + ox, top, bz + oz], [ax + ox, top, az + oz]], [0, 1, 0], capUv);
      u = next;
    }
  }
  const { frame, infill } = railingMaterials(style);
  const panelGeometry = geometry(panels);
  const framed = new THREE.Mesh(geometry({
    positions: [...panels.positions, ...caps.positions],
    normals: [...panels.normals, ...caps.normals],
    uvs: [...panels.uvs, ...caps.uvs],
  }), frame);
  framed.name = "RailingFrame";
  if (!infill) {
    panelGeometry.dispose();
    return [framed];
  }
  const filled = new THREE.Mesh(panelGeometry, infill);
  filled.name = "RailingInfill";
  return [framed, filled];
}

/** Toutes les barrières, regroupées par finition. */
export function buildRailings(edges: { style: RailingStyle; edges: RailingEdge[] }[], name: string): THREE.Group {
  const group = new THREE.Group();
  group.name = name;
  const byStyle = new Map<RailingStyle, RailingEdge[]>();
  for (const { style, edges: list } of edges) {
    if (!list.length) continue;
    const existing = byStyle.get(style);
    if (existing) existing.push(...list);
    else byStyle.set(style, [...list]);
  }
  for (const [style, list] of byStyle) group.add(...styleMeshes(style, list));
  return group;
}

/** Les tronçons d'un contour : fermé, il revient à son premier point. */
export function outlineEdges(points: GroundPoint[], closed: boolean, base: number): RailingEdge[] {
  const first = points[0];
  const last = points[points.length - 1];
  const open = closed && first && last && points.length > 1 && samePoint(first, last) ? points.slice(0, -1) : points;
  const count = closed ? open.length : open.length - 1;
  return Array.from({ length: Math.max(0, count) }, (_, index) => ({ a: open[index]!, b: open[(index + 1) % open.length]!, base }));
}
